import {
  ActionConfirmationModal,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  LoadingSpinner,
} from "@/components/ui";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/shadcn/Tabs/Tabs";
import { useUser } from "@/components/UserProvider";
import { useDashboardTableLiveUpdates } from "@/lib/data/live-updates/dashboard-tables/use-live-updates";
import {
  dashboardTabsQueryOptions,
  useDashboardTabMutations,
} from "@/lib/data/react-query/matters/dashboard-tabs";
import {
  dashboardTablesQueryOptions,
  useDashboardTableMutations,
} from "@/lib/data/react-query/matters/dashboard-tables";
import { queryKeys } from "@/lib/data/static/query-keys";
import type {
  DashboardTabView,
  MatterDashboardTab,
} from "@/lib/types/MatterDashboardTab";
import type {
  DashboardTableType,
  MatterDashboardTable,
} from "@/lib/types/MatterDashboardTable";
import { cn } from "@/lib/utils/cn";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import {
  ArrowUp,
  ArrowDown,
  Eye,
  ListOrdered,
  Pencil,
  Plus,
  Table2,
  Trash2,
  UserRound,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { CustomDashboardTable } from "./CustomDashboardTable";
import { DashboardTableEditorModal } from "./DashboardTableEditorModal";
import { DashboardTabEditorModal } from "./DashboardTabEditorModal";
import { DashboardTabReorderModal } from "./DashboardTabReorderModal";
import { UnassignedActionsTable } from "./UnassignedActionsTable";

type Editor =
  | { kind: "tab"; tab?: MatterDashboardTab }
  | { kind: "table"; tabId: string; table?: MatterDashboardTable };

type DeleteTarget =
  | { kind: "tab"; tab: MatterDashboardTab }
  | { kind: "table"; table: MatterDashboardTable };

export function CustomDashboardTabs({
  overview,
  selectedTabId,
  onSelectedTabIdChange,
}: {
  overview: ReactNode;
  selectedTabId: string;
  onSelectedTabIdChange: (tabId: string) => void;
}) {
  const { currentUser } = useUser();
  const queryClient = useQueryClient();
  const clearedAccessScope = useRef("");
  const [tabView, setTabView] = useState<DashboardTabView>("mine");
  const {
    data: tabResponse,
    isPending: tabsPending,
    isError: tabsError,
    error: tabQueryError,
    refetch,
  } = useQuery(
    dashboardTabsQueryOptions(currentUser.firmId, currentUser.id, tabView),
  );
  const { createTab, updateTab, reorderTabs, deleteTab } =
    useDashboardTabMutations(currentUser.firmId, currentUser.id);
  const { createTable, updateTable, reorderTables, deleteTable } =
    useDashboardTableMutations(currentUser.firmId, currentUser.id);
  const tabsAccessDenied =
    tabsError &&
    isAxiosError(tabQueryError) &&
    tabQueryError.response?.status === 403;
  const tabs = tabsAccessDenied ? [] : (tabResponse?.data ?? []);
  const [selectedTableByTab, setSelectedTableByTab] = useState<
    Record<string, string>
  >({});
  const [editor, setEditor] = useState<Editor | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftTeamIds, setDraftTeamIds] = useState<string[]>([]);
  const [draftUserIds, setDraftUserIds] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [createOutcomeUnknown, setCreateOutcomeUnknown] = useState(false);
  const selectedTabRecord = tabs.find((tab) => tab.id === selectedTabId);
  const selectedTab = selectedTabRecord;
  const {
    data: tablesResponse,
    isPending: tablesPending,
    isError: tablesError,
    error: tableQueryError,
    refetch: refetchTables,
  } = useQuery(
    dashboardTablesQueryOptions(
      currentUser.firmId,
      currentUser.id,
      selectedTab?.id ?? "",
    ),
  );
  const tablesAccessDenied =
    tablesError &&
    isAxiosError(tableQueryError) &&
    tableQueryError.response?.status === 403;
  const accessDenied = tabsAccessDenied || tablesAccessDenied;
  const visibleTabs = accessDenied ? [] : tabs;
  const tables = accessDenied ? [] : (tablesResponse?.data ?? []);
  const selectedTable =
    tables.find((table) => table.id === selectedTableByTab[selectedTabId]) ??
    tables[0];
  const liveUpdates = useDashboardTableLiveUpdates({
    firmId: currentUser.firmId,
    userId: currentUser.id,
    activeTabId: selectedTab?.id,
    tables: tables.length > 0 ? [selectedTable] : [],
    enabled: Boolean(currentUser.owner && !accessDenied),
  });

  useEffect(() => {
    if (!accessDenied) return;
    const scope = `${currentUser.firmId}:${currentUser.id}`;
    if (clearedAccessScope.current === scope) return;
    clearedAccessScope.current = scope;
    for (const key of [
      queryKeys.matterDashboardTabs,
      queryKeys.matterDashboardTables,
      queryKeys.matterDashboardRows,
      queryKeys.matterDashboardCells,
    ]) {
      queryClient.removeQueries({
        queryKey: [key, currentUser.firmId, currentUser.id],
      });
    }
    setEditor(null);
    setDeleteTarget(null);
    onSelectedTabIdChange("overview");
  }, [
    accessDenied,
    currentUser.firmId,
    currentUser.id,
    onSelectedTabIdChange,
    queryClient,
  ]);

  useEffect(() => {
    if (
      !tabsPending &&
      !tabsError &&
      selectedTabId !== "overview" &&
      !selectedTabRecord
    ) {
      onSelectedTabIdChange("overview");
    }
  }, [
    selectedTabId,
    selectedTabRecord,
    tabsError,
    tabsPending,
    onSelectedTabIdChange,
  ]);

  const openTabEditor = (tabId?: string) => {
    const tab = tabs.find((item) => item.id === tabId);
    setDraftTitle(tab?.title ?? "");
    setDraftTeamIds(tab?.teamIds ?? []);
    setDraftUserIds(
      tab?.userIds ?? (tabView === "mine" ? [currentUser.id] : []),
    );
    setError("");
    setCreateOutcomeUnknown(false);
    setEditor({ kind: "tab", tab });
  };

  const openTableEditor = (tabId: string, tableId?: string) => {
    setError("");
    setEditor({
      kind: "table",
      tabId,
      table: tables.find((item) => item.id === tableId),
    });
  };

  const saveTab = async () => {
    if (!editor || editor.kind !== "tab") return;
    const title = draftTitle.trim();
    if (!title) {
      setError("Tab name cannot be blank.");
      return;
    }
    try {
      if (editor.tab) {
        const tab = editor.tab;
        const response = await updateTab.mutateAsync({
          id: tab.id,
          dateModified: tab.dateModified,
          ...(title !== tab.title ? { title } : {}),
          ...(JSON.stringify([...draftTeamIds].sort()) !==
          JSON.stringify([...tab.teamIds].sort())
            ? { teamIds: draftTeamIds }
            : {}),
          ...(JSON.stringify([...draftUserIds].sort()) !==
          JSON.stringify([...tab.userIds].sort())
            ? { userIds: draftUserIds }
            : {}),
        });
        if (tabView === "mine" && !response.data.isMember) {
          setTabView("all");
        }
      } else {
        const response = await createTab.mutateAsync({
          title,
          teamIds: draftTeamIds,
          userIds: draftUserIds,
        });
        if (tabView === "mine" && !response.data.isMember) {
          setTabView("all");
        }
        onSelectedTabIdChange(response.data.id);
      }
    } catch (failure) {
      await refetch();
      const ambiguousCreate =
        !editor.tab && (!isAxiosError(failure) || !failure.response);
      setCreateOutcomeUnknown(ambiguousCreate);
      setError(
        ambiguousCreate
          ? "The response was lost, so this tab may have been created. Close this dialog and check the tab list before trying again."
          : isAxiosError(failure) && failure.response?.status === 409
            ? "This tab changed elsewhere. Close and reopen it to review the latest version."
            : "Unable to save the tab. Please try again.",
      );
      return;
    }
    setEditor(null);
  };

  const saveTable = async (value: {
    title: string;
    type?: DashboardTableType;
    phaseIds: string[];
    excludedActionDefinitionIds: string[];
    dataItemIds: string[];
    orderedColumnKeys?: string[];
  }) => {
    if (!editor || editor.kind !== "table") return;
    try {
      if (editor.table) {
        const table = editor.table;
        const body = {
          dateModified: table.dateModified,
          ...(value.title !== table.title ? { title: value.title } : {}),
          ...(JSON.stringify(value.phaseIds) !== JSON.stringify(table.phaseIds)
            ? { phaseIds: value.phaseIds }
            : {}),
          ...(JSON.stringify(value.excludedActionDefinitionIds) !==
          JSON.stringify(table.excludedActionDefinitionIds)
            ? { excludedActionDefinitionIds: value.excludedActionDefinitionIds }
            : {}),
          ...(JSON.stringify(value.dataItemIds) !==
          JSON.stringify(table.dataItemIds ?? [])
            ? { dataItemIds: value.dataItemIds }
            : {}),
          ...(value.orderedColumnKeys
            ? { orderedColumnKeys: value.orderedColumnKeys }
            : {}),
        };
        await updateTable.mutateAsync({
          tableId: table.id,
          body,
        });
      } else {
        const response = await createTable.mutateAsync({
          tabId: editor.tabId,
          body: value,
        });
        setSelectedTableByTab((current) => ({
          ...current,
          [editor.tabId]: response.data.id,
        }));
      }
    } catch (failure) {
      await refetchTables();
      throw failure;
    }
    setEditor(null);
  };

  const remove = async () => {
    if (!deleteTarget) return false;
    if (deleteTarget.kind === "tab") {
      const tab = deleteTarget.tab;
      try {
        await deleteTab.mutateAsync({
          id: tab.id,
          dateModified: tab.dateModified,
        });
        setEditor(null);
        onSelectedTabIdChange("overview");
      } catch (failure) {
        await refetch();
        if (isAxiosError(failure) && failure.response?.status === 409) {
          setDeleteTarget(null);
          setEditor(null);
        }
        setError(
          isAxiosError(failure) && failure.response?.status === 409
            ? "This tab changed elsewhere. Review it and open Delete again."
            : "Unable to delete the tab. Please try again.",
        );
        return false;
      }
    } else {
      const table = deleteTarget.table;
      try {
        await deleteTable.mutateAsync({
          tableId: table.id,
          dateModified: table.dateModified,
        });
        setSelectedTableByTab((current) => ({ ...current, [table.tabId]: "" }));
      } catch (failure) {
        await refetchTables();
        if (isAxiosError(failure) && failure.response?.status === 409) {
          setDeleteTarget(null);
        }
        setError(
          isAxiosError(failure) && failure.response?.status === 409
            ? "This table changed elsewhere. Review it and open Delete again."
            : "Unable to delete the table. Please try again.",
        );
        return false;
      }
    }
    setDeleteTarget(null);
    return true;
  };

  const saveTabOrder = async (reordered: MatterDashboardTab[]) => {
    if (tabView !== "all") return;
    try {
      await reorderTabs.mutateAsync(
        reordered.map(({ id, dateModified }) => ({ id, dateModified })),
      );
      setReorderOpen(false);
    } catch (failure) {
      await refetch();
      setError(
        isAxiosError(failure) && failure.response?.status === 409
          ? "Tabs changed elsewhere. Close and reopen Reorder tabs to review the latest order."
          : "Unable to reorder the tabs. Please try again.",
      );
    }
  };

  const moveTable = async (table: MatterDashboardTable, direction: -1 | 1) => {
    const index = tables.findIndex((item) => item.id === table.id);
    const target = index + direction;
    if (!selectedTab || target < 0 || target >= tables.length) return;
    const reordered = [...tables];
    [reordered[index], reordered[target]] = [
      reordered[target],
      reordered[index],
    ];
    try {
      await reorderTables.mutateAsync({
        tabId: selectedTab.id,
        body: {
          dateModified: selectedTab.dateModified,
          tables: reordered.map(({ id, dateModified }) => ({
            id,
            dateModified,
          })),
        },
      });
    } catch (failure) {
      await Promise.all([refetchTables(), refetch()]);
      setError(
        isAxiosError(failure) && failure.response?.status === 409
          ? "Tables changed elsewhere. Review the latest order before trying again."
          : "Unable to reorder the tables. Please try again.",
      );
    }
  };

  return (
    <div
      className={cn(
        "min-w-0",
        selectedTabId !== "overview" &&
          "flex min-h-0 flex-1 flex-col overflow-hidden",
      )}
    >
      <div className="flex shrink-0 items-end justify-between gap-3 border-b">
        <Tabs
          value={selectedTabId}
          onValueChange={onSelectedTabIdChange}
          className="min-w-0 flex-1"
        >
          <TabsList className="h-auto max-w-full justify-start gap-5 overflow-x-auto rounded-none bg-transparent p-0">
            <TabsTrigger
              value="overview"
              className="rounded-none bg-transparent px-0 pt-2 pb-3 focus-visible:underline focus-visible:decoration-2 focus-visible:underline-offset-4 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=active]:border-neutral-900 data-[state=active]:bg-transparent data-[state=active]:text-neutral-900 dark:data-[state=active]:border-white dark:data-[state=active]:text-white"
            >
              Overview
            </TabsTrigger>
            {visibleTabs.map((tab) => (
              <div key={tab.id} className="relative flex shrink-0 items-center">
                <TabsTrigger
                  value={tab.id}
                  className="rounded-none bg-transparent pt-2 pr-8 pb-3 pl-0 focus-visible:underline focus-visible:decoration-2 focus-visible:underline-offset-4 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=active]:border-neutral-900 data-[state=active]:bg-transparent data-[state=active]:text-neutral-900 dark:data-[state=active]:border-white dark:data-[state=active]:text-white"
                >
                  {tab.title}
                </TabsTrigger>
                {selectedTabId === tab.id && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-foreground absolute top-1 right-0 size-7"
                    aria-label={`Edit ${tab.title} tab`}
                    onClick={() => {
                      openTabEditor(tab.id);
                    }}
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </TabsList>
        </Tabs>
        <div className="mb-1 flex shrink-0 items-center gap-2">
          {currentUser.owner && (
            <Button
              variant="outline"
              size="sm"
              className="bg-background min-w-32 shrink-0 shadow-xs"
              aria-pressed={tabView === "all"}
              onClick={() => {
                setTabView((view) => (view === "mine" ? "all" : "mine"));
              }}
            >
              {tabView === "mine" ? <Eye /> : <UserRound />}
              {tabView === "mine" ? "Show all tabs" : "My tabs"}
            </Button>
          )}
          {currentUser.owner && tabView === "all" && tabs.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setError("");
                setReorderOpen(true);
              }}
            >
              <ListOrdered /> Reorder tabs
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              openTabEditor();
            }}
            disabled={accessDenied || !currentUser.owner}
          >
            <Plus /> Add tab
          </Button>
        </div>
      </div>
      {tabsPending && (
        <div
          className="flex min-h-28 items-center justify-center"
          role="status"
          aria-label="Loading dashboard tabs"
        >
          <LoadingSpinner />
        </div>
      )}
      {accessDenied && (
        <p className="text-destructive py-3 text-sm" role="alert">
          Dashboard access is no longer available.
        </p>
      )}
      {tabsError && !accessDenied && (
        <div className="flex items-center gap-2 py-3 text-sm" role="alert">
          Unable to load dashboard tabs.
          <Button size="sm" variant="ghost" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      )}
      {error && !editor && (
        <p className="text-destructive py-2 text-sm" role="alert">
          {error}
        </p>
      )}
      {!tabsPending &&
        !tabsError &&
        tabView === "mine" &&
        tabs.length === 0 && (
          <p className="text-muted-foreground py-3 text-sm">
            No tabs assigned to you. Use show all tabs to view every firm tab.
          </p>
        )}
      {selectedTabId === "overview" && <div className="pt-5">{overview}</div>}
      {selectedTab && !accessDenied && (
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-hidden pt-5">
          <h2 className="shrink-0 text-xl font-semibold">
            {selectedTab.title}
          </h2>
          {tablesPending ? (
            <div
              className="flex min-h-52 items-center justify-center"
              role="status"
              aria-label="Loading tables"
            >
              <LoadingSpinner />
            </div>
          ) : tablesError ? (
            <div
              className="flex items-center justify-center gap-2 py-8 text-sm"
              role="alert"
            >
              Unable to load tables.
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void refetchTables()}
              >
                Try again
              </Button>
            </div>
          ) : tables.length === 0 ? (
            <div className="flex min-h-52 flex-col items-center justify-center gap-3 rounded-xl border border-dashed p-6 text-center">
              <p className="font-medium">No tables in this tab yet</p>
              <p className="text-muted-foreground text-sm">
                Add a table, then choose phases and the actions to show.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  openTableEditor(selectedTab.id);
                }}
              >
                <Plus /> Add table
              </Button>
            </div>
          ) : (
            <Tabs
              value={selectedTable.id}
              onValueChange={(tableId) => {
                setSelectedTableByTab((current) => ({
                  ...current,
                  [selectedTab.id]: tableId,
                }));
              }}
              orientation="vertical"
              className="grid min-h-0 min-w-0 flex-1 grid-cols-[11rem_minmax(0,1fr)] grid-rows-[minmax(0,1fr)] gap-5"
            >
              <div className="min-h-0 min-w-0 overflow-y-auto">
                <TabsList
                  className="flex h-auto w-full flex-col items-stretch justify-start gap-1 bg-transparent p-0"
                  aria-label="Dashboard tables"
                >
                  {tables.map((table, index) => (
                    <div
                      key={table.id}
                      className="relative w-full min-w-0 shrink-0"
                    >
                      <TabsTrigger
                        value={table.id}
                        className="text-muted-foreground hover:bg-muted/60 w-full justify-start gap-2 rounded-md border-0 bg-transparent py-2.5 pr-9 pl-3 text-left font-normal focus-visible:ring-neutral-900 focus-visible:ring-offset-0 focus-visible:ring-inset data-[state=active]:rounded-md data-[state=active]:border-0 data-[state=active]:bg-neutral-900 data-[state=active]:text-white dark:focus-visible:ring-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-neutral-900"
                      >
                        <Table2 className="size-4 shrink-0" />
                        <span className="min-w-0 truncate">{table.title}</span>
                      </TabsTrigger>
                      {selectedTable.id === table.id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-1 right-1 size-8 text-white hover:bg-neutral-700 hover:text-white dark:text-neutral-900 dark:hover:bg-neutral-200 dark:hover:text-neutral-900"
                              aria-label={`Edit ${table.title} table`}
                            >
                              <Pencil className="size-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            <DropdownMenuItem
                              onSelect={() => {
                                openTableEditor(selectedTab.id, table.id);
                              }}
                            >
                              <Pencil /> Edit table
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={index === 0 || reorderTables.isPending}
                              onSelect={() => void moveTable(table, -1)}
                            >
                              <ArrowUp /> Move up
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={
                                index === tables.length - 1 ||
                                reorderTables.isPending
                              }
                              onSelect={() => void moveTable(table, 1)}
                            >
                              <ArrowDown /> Move down
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onSelect={() => {
                                setError("");
                                setDeleteTarget({
                                  kind: "table",
                                  table,
                                });
                              }}
                            >
                              <Trash2 /> Delete table
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  ))}
                </TabsList>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground mt-2 w-full justify-start"
                  onClick={() => {
                    openTableEditor(selectedTab.id);
                  }}
                >
                  <Plus /> Add table
                </Button>
              </div>
              <TabsContent
                value={selectedTable.id}
                className="m-0 flex h-fit max-h-full min-h-0 min-w-0 flex-col self-start overflow-hidden rounded-lg border focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
              >
                {selectedTable.type === "unassigned_actions" ? (
                  <UnassignedActionsTable
                    key={selectedTable.id}
                    table={selectedTable}
                    firmId={currentUser.firmId}
                    userId={currentUser.id}
                    ready={liveUpdates.ready}
                    afterSequence={liveUpdates.afterSequence}
                  />
                ) : (
                  <CustomDashboardTable
                    key={selectedTable.id}
                    table={selectedTable}
                    firmId={currentUser.firmId}
                    userId={currentUser.id}
                    ready={liveUpdates.ready}
                    afterSequence={liveUpdates.afterSequence}
                  />
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      )}
      {editor?.kind === "tab" && (
        <DashboardTabEditorModal
          key={editor.tab?.id ?? "new"}
          tabId={editor.tab?.id}
          title={draftTitle}
          originalTitle={editor.tab?.title ?? ""}
          setTitle={setDraftTitle}
          teamIds={draftTeamIds}
          userIds={draftUserIds}
          originalTeamIds={editor.tab?.teamIds ?? []}
          originalUserIds={editor.tab?.userIds ?? []}
          setTeamIds={setDraftTeamIds}
          setUserIds={setDraftUserIds}
          error={error}
          setError={setError}
          onClose={() => {
            setEditor(null);
            setCreateOutcomeUnknown(false);
          }}
          onSave={() => void saveTab()}
          onDelete={() => {
            const tab = editor.tab;
            if (!tab) return;
            setError("");
            setDeleteTarget({ kind: "tab", tab });
          }}
          saving={createTab.isPending || updateTab.isPending}
          saveDisabled={createOutcomeUnknown}
        />
      )}
      {reorderOpen && (
        <DashboardTabReorderModal
          tabs={tabs}
          saving={reorderTabs.isPending}
          error={error}
          onClose={() => {
            setReorderOpen(false);
            setError("");
          }}
          onSave={(ordered) => void saveTabOrder(ordered)}
        />
      )}
      {editor?.kind === "table" && (
        <DashboardTableEditorModal
          key={`${editor.tabId}:${editor.table?.id ?? "new"}`}
          firmId={currentUser.firmId}
          userId={currentUser.id}
          table={editor.table}
          onClose={() => {
            setEditor(null);
          }}
          onSave={saveTable}
        />
      )}
      <ActionConfirmationModal
        open={deleteTarget !== null}
        setOpen={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={`Delete ${deleteTarget?.kind === "table" ? "table" : "tab"}?`}
        description={
          <>
            {deleteTarget?.kind === "tab"
              ? `Delete “${deleteTarget.tab.title}” for the firm? All tables in it will be deleted too.`
              : `Delete the table “${deleteTarget?.table.title ?? ""}” for the firm?`}
            {error && (
              <span className="text-destructive mt-2 block" role="alert">
                {error}
              </span>
            )}
          </>
        }
        buttonText="Delete"
        afterActionFn={remove}
      />
    </div>
  );
}
